import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";

const API =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL;

/**
 * Public Waitlist unsubscribe confirmation page.
 * GET /waitlist/abmelden?token=... renders this page.
 * User must click the "Abmelden" button which sends POST /api/waitlist/unsubscribe.
 * This prevents automatic mail-prefetch unsubscribes.
 */
export default function WaitlistAbmeldenPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | loading | done | error

  const onUnsubscribe = async (e) => {
    e.preventDefault();
    if (!token || state === "loading" || state === "done") return;
    setState("loading");
    try {
      const r = await fetch(`${API}/api/waitlist/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (r.ok) setState("done");
      else setState("error");
    } catch {
      setState("error");
    }
  };

  return (
    <main
      className="min-h-screen bg-ag-paper text-ag-ink"
      data-testid="waitlist-abmelden-page"
    >
      <header className="mx-auto flex max-w-6xl items-center px-5 py-6 lg:px-8">
        <Link to="/" className="flex items-center" aria-label="Zur Startseite">
          <Logo variant="full" tone="light" className="h-9 w-auto md:h-10" />
        </Link>
      </header>

      <section className="mx-auto max-w-xl px-5 py-20 text-center lg:py-28">
        <div className="mx-auto mb-8 h-px w-16 bg-amber-500/70" aria-hidden="true" />
        <h1 className="font-serif text-3xl leading-tight text-ag-ink sm:text-4xl lg:text-5xl">
          Von der Warteliste abmelden
        </h1>

        {state !== "done" ? (
          <>
            <p className="mt-6 text-base leading-relaxed text-slate-700 sm:text-[17px]">
              Wenn Sie sich abmelden, informieren wir Sie nicht mehr über den
              Start von ALLES GEREGELT. Sie können sich später jederzeit wieder
              anmelden.
            </p>

            <form
              onSubmit={onUnsubscribe}
              className="mt-10"
              data-testid="waitlist-abmelden-form"
              aria-live="polite"
            >
              <button
                type="submit"
                disabled={!token || state === "loading"}
                className="inline-flex min-h-12 items-center justify-center rounded-full bg-ag-ink px-8 text-sm font-medium text-white shadow-sm transition hover:bg-ag-ink/90 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="waitlist-abmelden-submit"
              >
                {state === "loading" ? "Wird abgemeldet …" : "Jetzt abmelden"}
              </button>

              {!token && (
                <p className="mt-6 text-sm text-slate-500" role="status">
                  Der Abmeldelink ist unvollständig. Bitte verwenden Sie den
                  Link aus Ihrer E-Mail.
                </p>
              )}
              {state === "error" && (
                <p className="mt-6 text-sm text-slate-600" role="status">
                  Wir konnten Ihre Abmeldung gerade nicht abschließen. Bitte
                  versuchen Sie es später erneut.
                </p>
              )}
            </form>
          </>
        ) : (
          <div aria-live="polite">
            <p className="mt-6 text-base leading-relaxed text-slate-700 sm:text-[17px]">
              Sie sind erfolgreich von der Warteliste abgemeldet. Wir werden
              Sie nicht weiter kontaktieren.
            </p>
            <Link
              to="/"
              className="mt-10 inline-flex min-h-12 items-center justify-center rounded-full border border-ag-ink/70 px-6 text-sm font-medium text-ag-ink transition hover:bg-ag-ink/5"
              data-testid="waitlist-abmelden-home"
            >
              Zur Startseite
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
